import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { areasAPI } from '../utils/api';
import { useAuth } from './AuthContext';

export interface SidebarArea {
  id: number;
  name: string;
  cameras_count?: number;
  cameras_online?: number;
}

interface AreasContextType {
  areas: SidebarArea[];
  loading: boolean;
  /** Перезагрузить список участков (например, после создания/удаления). */
  refreshAreas: () => Promise<void>;
}

const AreasContext = createContext<AreasContextType | undefined>(undefined);

/**
 * Список участков для навигации.
 *
 * Отдельный лёгкий контекст нужен боковому меню, чтобы показывать
 * вложенные разделы. Страницы продолжают работать со своими данными —
 * контекст ничего не подменяет и лишь дублирует чтение списка.
 */
export const useAreasNav = (): AreasContextType => {
  const context = useContext(AreasContext);
  if (!context) {
    return { areas: [], loading: false, refreshAreas: async () => {} };
  }
  return context;
};

interface AreasProviderProps {
  children: ReactNode;
}

export const AreasProvider: React.FC<AreasProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [areas, setAreas] = useState<SidebarArea[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const refreshAreas = useCallback(async () => {
    if (!userId) {
      setAreas([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      const response = await areasAPI.getAll();
      if (requestId !== requestIdRef.current) return;
      if (response.success) {
        setAreas(response.areas);
      }
    } catch {
      /* навигация не критична: при ошибке просто не показываем вложенный список */
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    // Сразу убираем чужие участки (вход под пользователем / возврат в админку).
    setAreas([]);
    if (userId) {
      void refreshAreas();
    }
  }, [userId, refreshAreas]);

  const value = useMemo(
    () => ({ areas, loading, refreshAreas }),
    [areas, loading, refreshAreas]
  );

  return <AreasContext.Provider value={value}>{children}</AreasContext.Provider>;
};
